/**
 * DECISIONS 198: the soldier rebuilt to the reference's bar, on today's rig. The owner, Oct 18: "here's the model as a
 * reference for the soldier. What I'm thinking is to generally aim towards something of that detail." … "in general I
 * think I want the base soldier to be wearing no special equipment, with no armor just the plate carrier, then the light
 * and heavy armor and helmet on top, I think that's a good system". On the options sheet (design_sheets/soldier.html) he
 * picked B: "For humans I think B is probably best but shoulder and waist could use some work, and we'll want to do
 * better animations, but I think that's probably the one to work with."
 *
 * The body is no longer boxes: each part is a loft (rings of a few sides, tapered — a thigh wide at the seat and narrow at
 * the knee, a calf, a chest broadening into the shoulders over a narrower waist, a jaw), with chamfered slabs for the
 * plates and pouches and side profiles for the boots (the D guns' style). Every piece goes on a group that already moves,
 * coloured per vertex, so it bakes into that group's draw: no new draws. The belt and the pelvis ride the hips (with the
 * holster), so a lean bends at the waist and the pelvis stays with the legs.
 *
 * The layers (`SoldierDress`):
 * - **plain** — the base soldier: a combat shirt, darker trousers and a belt, boots, gloves, the face behind a gaiter and
 *   glasses in the team's flash; no equipment. The sheets show it; in play every figure wears at least the carrier.
 * - **carrier** (no armour issued) — a slick plate carrier in soft cloth: panels, straps, a cummerbund, the magazines, an
 *   admin pouch, the assault pack on the back (the team colour from behind).
 * - **light** — the carrier with its plates in: hard plate bags, side plates in the cummerbund, a team patch.
 * - **heavy** — over the light: a throat guard and a collar, deltoid pads, a groin flap, heavy side plates.
 * - **helmet** — none (a balaclava), light (high-cut, a headset, a shroud, rails, a counterweight), heavy (full-cut over
 *   the ears, a mandible guard); each with the leader's variant (the band in the flash, a boom mic, an antenna, rank bars).
 * - **leader** — 68.8's kit refitted: the radio on the pack and its whip, the slate, the handset, the binocular case.
 * - **wear** — 67.56's worn and broken looks (armourLooks.ts `makeKit`) on the plates, pads and helmets.
 *
 * Person units (a person is 1.8), the model's frame: +z front, +x the figure's left (the gun arm as built; 67.21 mirrors
 * the root for a right hand). The light helmet keeps the old shell's envelope (its top 0.33 over the head's joint, its back
 * pouch where it was), so the person's hit capsule and the cover poses' tops hold. Rendering only.
 */
import * as THREE from 'three';
import { CC, mix, loft, slab, cbox, piece, bar, type Ring } from './soldierKit';
import { helmet, comtac } from './helmetLooks';
import type { PersonModel } from './personModel';
import type { UnitPalette } from './unitPalette';
import { armourColours, makeKit, type Kit, type HelmetKind, type VestKind, type ArmourWear } from './armourLooks';
import { crewHelmet, dressRole, roleReplacesPack, type RoleDress } from './roleLooks';

/** What the soldier wears. `plain`: the base soldier alone (no carrier, no vest). */
export interface SoldierDress { plain?: boolean; vest: VestKind; helmet: HelmetKind; leader?: boolean; wear?: ArmourWear; seed?: number;
  /** 201 (8): the medic's or the engineer's kit over the carrier (roleLooks.ts) */
  role?: RoleDress }

/** The neutral colours (the team's are the palette's). */
export const SOLDIER_CLOTH = { glove: '#5b4d40', boot: '#3d342b', sole: '#1f1d1a', skin: '#a47c5e', knit: '#4b4c44', hair: '#3a2e25' };
/** The sides round a limb, the torso, the head and the helmet (B, the owner's pick). */
const SIDES = { limb: 6, torso: 10, head: 6 };

// ---- the body --------------------------------------------------------------------------------------------------------

/** The cloth in a team's colours: the shirt the body colour, the trousers well darker (the two read apart), the belt dark. */
export interface Cloth { shirt: string; pants: string; belt: string; gaiter: string; flash: string; dark: string; body: string; metal: string }
export function clothOf(p: UnitPalette): Cloth {
  return { shirt: p.body, pants: mix(p.body, p.dark, 0.55), belt: p.dark, gaiter: mix(p.dark, '#3a3a36', 0.5), flash: p.flash, dark: p.dark, body: p.body, metal: p.metal };
}

/** The belt and the pelvis, on the hips (the legs' parent): the seat, the belt round the waist, its buckle. */
function buildPelvis(h: THREE.Group, k: Cloth): void {
  loft(h, [
    { y: -0.105, rx: 0.1, rz: 0.085, c: k.pants },                    // the crotch, up between the thighs
    { y: -0.06, rx: 0.195, rz: 0.13, z: -0.005, c: k.pants },         // the seat and the hips
    { y: 0.0, rx: 0.2, rz: 0.132, c: k.belt },                        // the belt
    { y: 0.075, rx: 0.198, rz: 0.13, c: k.belt },
    { y: 0.1, rx: 0.17, rz: 0.11 },                                   // tucked in under the shirt
  ], SIDES.torso, k.pants, true, false);
  cbox(h, 0.06, 0.05, 0.02, k.metal, 0, 0.038, 0.135);              // the buckle
}

/**
 * The shirt over the torso (from the waist, inside the belt, so a lean bends there) to the neck: a waist narrower than the
 * chest, the chest broadening into the armpits, the yoke sloping from the neck out to the shoulders' tops (x ≈ 0.27, where
 * the arm's deltoid meets it, not a step).
 */
function buildTorso(t: THREE.Group, k: Cloth): void {
  loft(t, [
    { y: 0.03, rx: 0.18, rz: 0.12 },
    { y: 0.17, rx: 0.188, rz: 0.128, z: 0.005 },                      // the waist
    { y: 0.31, rx: 0.222, rz: 0.142, z: 0.012 },
    { y: 0.43, rx: 0.248, rz: 0.15, z: 0.015 },                       // the chest
    { y: 0.52, rx: 0.268, rz: 0.142, z: 0.006 },                      // the armpits, broadening into the shoulders
    { y: 0.59, rx: 0.262, rz: 0.12, z: -0.006 },                      // the shoulders' tops
    { y: 0.64, rx: 0.15, rz: 0.092, z: -0.01 },                       // the yoke to the neck
    { y: 0.672, rx: 0.075, rz: 0.075 },
  ], SIDES.torso, k.shirt, false, false).userData.part = 'shirt';
  loft(t, [{ y: 0.6, rx: 0.1, rz: 0.1, z: 0.005 }, { y: 0.678, rx: 0.088, rz: 0.088, z: 0.005 }], SIDES.head, k.shirt, false, false);   // the collar
}

/**
 * The head: the neck and the lower face in a gaiter pulled up over the nose (the owner's pick of the face, option C), the eye
 * line in the brim's shadow above it — no drawn eyes — and under a helmet the crown left out (its lining hides it). With no
 * helmet, a balaclava: a knit hood with its eye slit, under the headset.
 */
function buildHead(h: THREE.Group, k: Cloth, hooded: boolean, helmeted: boolean): void {
  const s = hooded ? 0.012 : 0;
  const face = hooded ? SOLDIER_CLOTH.knit : SOLDIER_CLOTH.skin, low = hooded ? SOLDIER_CLOTH.knit : k.gaiter, top = hooded ? SOLDIER_CLOTH.knit : SOLDIER_CLOTH.hair;
  const shade = mix(SOLDIER_CLOTH.skin, '#000000', 0.42);
  const rings: Ring[] = [
    { y: -0.05, rx: 0.062 + s, rz: 0.066 + s, c: low },               // the neck (in the collar)
    { y: 0.045, rx: 0.085 + s, rz: 0.09 + s, z: 0.02, c: low },       // under the jaw
    { y: 0.085, rx: 0.1 + s, rz: 0.11 + s, z: 0.02, c: low },         // the gaiter, up over the nose
    ...(hooded ? [{ y: 0.14, rx: 0.11 + s, rz: 0.12 + s, z: 0.01, c: face }] : [
      { y: 0.118, rx: 0.106, rz: 0.116, z: 0.014, c: shade },         // the eye line, in the brim's shadow
      { y: 0.15, rx: 0.109, rz: 0.119, z: 0.008, c: face },
    ]),
    { y: 0.2, rx: 0.105 + s, rz: 0.115 + s, c: top },
    { y: 0.25, rx: 0.075 + s, rz: 0.085 + s, c: top },
    { y: 0.278 + s, rx: 0.03, rz: 0.035, c: top },
  ];
  const sides = hooded ? 8 : SIDES.head;
  loft(h, helmeted ? rings.slice(0, rings.length - 2) : rings, sides, face, false, !helmeted);
  if (!hooded) return;
  cbox(h, 0.15, 0.042, 0.03, SOLDIER_CLOTH.skin, 0, 0.132, 0.13 + s, 0.006);   // the eye slit
  loft(h, [{ y: 0.0, rx: 0.08 + s, rz: 0.084 + s }, { y: 0.04, rx: 0.074 + s, rz: 0.078 + s }], sides, SOLDIER_CLOTH.knit, false, false);   // the roll at the neck
  // the headset over the hood: its cups, the band over the crown
  comtac(h, k.metal, 0.134, 0.11);
  for (const sx of [-1, 1]) bar(h, [sx * 0.14, 0.14, -0.004], [sx * 0.11, 0.26, -0.004], 0.016, k.metal);
  bar(h, [-0.11, 0.26, -0.004], [0, 0.305, -0.004], 0.016, k.metal); bar(h, [0, 0.305, -0.004], [0.11, 0.26, -0.004], 0.016, k.metal);
}

function buildArmParts(arm: { group: THREE.Group; fore: THREE.Group }, k: Cloth, out: number, band: boolean): void {
  // the deltoid tucked in toward the torso's shoulder top (it meets the yoke there), the arm tapering to the elbow
  loft(arm.group, [
    { y: 0.075, rx: 0.055, rz: 0.06, x: -out * 0.02 },
    { y: 0.05, rx: 0.1, rz: 0.102, x: out * 0.004 },                 // the deltoid, meeting the yoke
    { y: -0.04, rx: 0.098, rz: 0.1 },
    { y: -0.12, rx: 0.084, rz: 0.088 },
    { y: -0.21, rx: 0.074, rz: 0.08 },
    { y: -0.345, rx: 0.066, rz: 0.072 },                              // the elbow
  ], SIDES.limb, k.shirt, false, false);
  if (band) loft(arm.group, [{ y: -0.1, rx: 0.087, rz: 0.091 }, { y: -0.155, rx: 0.082, rz: 0.088 }], SIDES.limb, k.flash, false, false);   // the armband: the flash on the side
  loft(arm.fore, [
    { y: 0.04, rx: 0.064, rz: 0.068 },
    { y: -0.04, rx: 0.07, rz: 0.075 },
    { y: -0.225, rx: 0.056, rz: 0.06, c: k.dark },                    // the cuff
    { y: -0.26, rx: 0.06, rz: 0.064 },
  ], SIDES.limb, k.shirt, false, false);
  // the glove: the back of the hand round the palm point (the IK closes it on the grip there), a thumb on the inside
  const inward = -out, G = SOLDIER_CLOTH.glove;
  loft(arm.fore, [
    { y: -0.245, rx: 0.042, rz: 0.05, z: 0.005 },
    { y: -0.29, rx: 0.05, rz: 0.062, z: 0.01 },
    { y: -0.345, rx: 0.05, rz: 0.058, z: 0.012 },
    { y: -0.39, rx: 0.038, rz: 0.046, z: 0.016 },
  ], SIDES.limb, G, false, true);
  const thumb = cbox(arm.fore, 0.038, 0.075, 0.048, G, inward * 0.05, -0.305, 0.05); thumb.rotation.z = inward * 0.35;
}

function buildLegParts(thigh: THREE.Group, shin: THREE.Group, foot: THREE.Group, k: Cloth, out: number): void {
  loft(thigh, [
    { y: 0.07, rx: 0.1, rz: 0.12 },
    { y: -0.02, rx: 0.12, rz: 0.133 },
    { y: -0.22, rx: 0.11, rz: 0.118, z: 0.005 },
    { y: -0.475, rx: 0.086, rz: 0.096 },
  ], SIDES.limb, k.pants, false, false);
  cbox(thigh, 0.03, 0.15, 0.14, k.pants, out * 0.115, -0.21, 0.005);   // the cargo pocket on the outside
  loft(shin, [
    { y: 0.06, rx: 0.088, rz: 0.098 },
    { y: -0.04, rx: 0.088, rz: 0.1, z: 0.005 },                       // the knee
    { y: -0.17, rx: 0.082, rz: 0.096, z: -0.008 },                    // the calf
    { y: -0.345, rx: 0.08, rz: 0.088 },                               // bloused over the boot
    { y: -0.37, rx: 0.074, rz: 0.082 },
  ], SIDES.limb, k.pants, false, true);
  // the boot: a side profile (heel to toe; the sole on the ground at y −0.06 under the ankle), no longer than the old boot
  // (its toe 0.195 ahead of the ankle: a prone body's pointed toes stay out of the ground)
  slab(foot, [-0.1, -0.036, 0.182, -0.036, 0.192, -0.02, 0.17, 0.005, 0.11, 0.035, 0.06, 0.06, 0.055, 0.1, -0.075, 0.1, -0.095, 0.03, -0.108, -0.02], 0.155, SOLDIER_CLOTH.boot);
  slab(foot, [-0.106, -0.06, 0.188, -0.06, 0.195, -0.036, -0.106, -0.036], 0.162, SOLDIER_CLOTH.sole);
}

// ---- the carrier and the armour --------------------------------------------------------------------------------------

/** The shirt's front and back (z) at heights up the torso (`buildTorso`'s rings, centre lines): what the carrier lies on. */
const CHEST = { y: [0.16, 0.31, 0.43, 0.56], front: [0.132, 0.154, 0.165, 0.137], back: [-0.122, -0.13, -0.135, -0.13] };
const along = (ys: number[], zs: number[], y: number): number => {
  if (y <= ys[0]) return zs[0];
  for (let i = 1; i < ys.length; i++) if (y <= ys[i]) return zs[i - 1] + (zs[i] - zs[i - 1]) * (y - ys[i - 1]) / (ys[i] - ys[i - 1]);
  return zs[zs.length - 1];
};
/** How thick the carrier's panels are: a soft bag, or with its plate in. */
const panelThick = (vest: VestKind) => (vest !== 'none' ? 0.05 : 0.026);
/** The carrier's front face at height `y` (its pouches sit on it). */
export function carrierFront(vest: VestKind, y = 0.43): number { return along(CHEST.y, CHEST.front, y) + panelThick(vest); }

function carrier(m: PersonModel, p: UnitPalette, k: Kit, vest: VestKind, leader: boolean, role?: RoleDress): void {
  const { c } = k, t = m.torso, plated = vest !== 'none';
  const panel = plated ? c.plate : c.soft, thick = panelThick(vest);
  // the front and back panels lie on the chest and the back (CHEST), a soft bag or with its plate in; worn and broken
  // plates take 67.56's marks
  const profile = (zs: number[], sign: number) => {
    const pts: number[] = [];
    CHEST.y.forEach((y, i) => pts.push(zs[i] - sign * 0.004, y));                                  // the inner face, on the shirt
    for (let i = CHEST.y.length - 1; i >= 0; i--) pts.push(zs[i] + sign * thick, CHEST.y[i]);      // the outer face
    return pts;
  };
  for (const [face, zs, sign] of [['front', CHEST.front, 1], ['back', CHEST.back, -1]] as const) {
    if (k.wear === 'whole' || !plated) { slab(t, profile(zs, sign), 0.38, panel, 0, face === 'front' ? CC : 0); continue; }
    k.plate(t, 0.38, 0.38, thick + 0.012, panel, 0, 0.36, along(CHEST.y, zs, 0.36) + sign * (thick / 2), face);
  }
  // the shoulder straps over the yoke, lying on it (the shirt's top at x 0.13 is about 0.65 high)
  for (const sx of [-1, 1]) slab(t, [0.135, 0.535, 0.152, 0.555, 0.06, 0.668, -0.06, 0.668, -0.152, 0.555, -0.135, 0.535, -0.04, 0.648, 0.04, 0.648], 0.085, c.soft, sx * 0.13);
  // the cummerbund round the waist
  loft(t, [{ y: 0.16, rx: 0.2, rz: 0.138, z: 0.005 }, { y: 0.35, rx: 0.242, rz: 0.156, z: 0.013 }], SIDES.torso, c.soft, false, false);
  if (plated) for (const sx of [-1, 1]) piece(k, 'plate', t, 0.035, 0.18, 0.17, vest === 'heavy' ? c.heavyPlate : c.plate, sx * 0.236, 0.26, 0.01, sx > 0 ? 'right' : 'left');   // the side plates
  // the shingle: three magazines in the team's colour, on the panel's face
  for (const x of [-0.115, 0, 0.115]) piece(k, 'pad', t, 0.1, 0.13, 0.05, c.pouch, x, 0.27, carrierFront(vest, 0.27) + 0.025, 'front');
  if (!leader) {   // the admin pouch, the team's patch on it (the leader carries the slate here)
    const az = carrierFront(vest, 0.45) + 0.013;
    if (k.wear === 'whole') cbox(t, 0.21, 0.08, 0.026, c.soft, 0, 0.45, az);
    else k.pad(t, 0.21, 0.08, 0.026, c.soft, 0, 0.45, az, 'front');
    cbox(t, 0.08, 0.045, 0.008, c.patch, 0.05, 0.455, az + 0.016);
  }
  // the assault pack on the back: body-coloured so the team reads from behind, narrower than the old one and without its
  // side pockets, its flap a shade darker (its back where the old pack's was: the top of a body prone at a crest, COVER_POSE_SIZE)
  // (201 (8): the medic's rucksack goes in its place)
  if (!roleReplacesPack(role)) {
    cbox(t, 0.3, 0.34, 0.17, p.body, 0, 0.34, -0.245);                // (its front on the back panel: −0.16)
    cbox(t, 0.31, 0.07, 0.19, mix(p.body, p.dark, 0.45), 0, 0.5, -0.245);
    cbox(t, 0.2, 0.012, 0.01, p.dark, 0, 0.28, -0.333);                // a compression strap
  }
  if (vest !== 'heavy') return;
  // heavy: the throat guard and the collar, deltoid pads, the groin flap
  // the collar stands on the yoke round the neck (its sides tuck into the shoulders, its front and back stand proud)
  loft(t, [{ y: 0.625, rx: 0.165, rz: 0.13, z: -0.005 }, { y: 0.685, rx: 0.135, rz: 0.115, z: -0.005 }], SIDES.torso, c.soft, false, false);
  slab(t, [0.11, 0.56, 0.17, 0.56, 0.168, 0.665, 0.115, 0.68], 0.24, c.plate, 0, CC);   // the throat guard, on the chest under the collar (the jaw clear above it)
  for (const arm of [m.armL, m.armR]) {
    // the deltoid pads over the deltoid (`buildArmParts`: 0.1 round at 0.05), hanging a little proud below it
    const out = Math.sign(arm.group.position.x);
    loft(arm.group, [{ y: 0.1, rx: 0.06, rz: 0.066, x: -out * 0.01 }, { y: 0.06, rx: 0.118, rz: 0.12, x: out * 0.006 }, { y: -0.06, rx: 0.113, rz: 0.116, x: out * 0.004 }, { y: -0.16, rx: 0.1, rz: 0.104 }], SIDES.limb, c.soft, false, true);
  }
  const groin = cbox(m.hips, 0.22, 0.17, 0.035, c.soft, 0, -0.06, 0.16, CC); groin.rotation.x = -0.12;   // on the hips, with the legs
  if (k.wear !== 'whole') groin.rotation.z = k.wear === 'broken' ? 0.25 : 0.06;
}

/** 68.8's leader kit, refitted to the carrier: the radio and its whip, the slate, the handset, the binocular case. */
function leaderKit(m: PersonModel, p: UnitPalette, front: number): void {
  const t = m.torso;
  cbox(t, 0.22, 0.13, 0.15, p.dark, -0.08, 0.615, -0.255, 0.008);
  cbox(t, 0.05, 0.04, 0.05, p.metal, -0.15, 0.7, -0.29);
  bar(t, [-0.15, 0.71, -0.29], [-0.21, 1.48, -0.42], 0.022, p.dark).userData.part = 'antenna';
  cbox(t, 0.04, 0.04, 0.04, p.dark, -0.21, 1.48, -0.42);
  cbox(t, 0.17, 0.11, 0.025, p.metal, 0.03, 0.45, front + 0.015, 0.008);
  cbox(t, 0.13, 0.075, 0.008, p.flash, 0.03, 0.455, front + 0.03);
  cbox(t, 0.05, 0.11, 0.04, p.metal, -0.15, 0.53, front - 0.01, 0.008);
  bar(t, [-0.15, 0.6, front - 0.02], [-0.12, 0.64, -0.2], 0.012, p.dark);   // the handset's cord to the radio
  cbox(m.hips, 0.08, 0.13, 0.13, p.dark, -0.27, 0.03, 0.03, 0.008);          // the binocular case at the support hip
}

/** Build the soldier on `m`'s groups (buildPersonModel calls it). Returns the parts added. */
export function buildSoldier(m: PersonModel, p: UnitPalette, s: SoldierDress): number {
  const cloth = clothOf(p), k = makeKit(armourColours(p, 'dark'), s.wear ?? 'whole', s.seed ?? 1);
  let before = 0; m.full.traverse(q => { if ((q as THREE.Mesh).isMesh) before++; });
  buildPelvis(m.hips, cloth);
  buildTorso(m.torso, cloth);
  buildHead(m.head, cloth, s.helmet === 'none', s.helmet !== 'none');
  for (const [arm, band] of [[m.armL, false], [m.armR, true]] as const) buildArmParts(arm, cloth, Math.sign(arm.group.position.x), band);
  buildLegParts(m.thighL, m.shinL, m.footL, cloth, Math.sign(m.thighL.position.x));
  buildLegParts(m.thighR, m.shinR, m.footR, cloth, Math.sign(m.thighR.position.x));
  // 69.46 (the sheet's look A): the crewman wears the padded crew helmet in the line helmet's place and, with no plates
  // issued, no plate carrier (nor its pack): his tool roll on the belt
  const crew = s.role?.role === 'crewman', crewLookA = crew && s.vest === 'none' && !s.leader;
  if (s.helmet !== 'none') { if (crew) crewHelmet(m); else helmet(m.head, k, s.helmet === 'heavy', s.leader === true); }
  if (crewLookA && !s.plain) dressRole(m, p, s.role!, s.helmet, () => 0);
  else if (!s.plain) {
    carrier(m, p, k, s.vest, s.leader === true, s.role);
    if (s.leader) leaderKit(m, p, carrierFront(s.vest, 0.45));
    if (s.role) dressRole(m, p, s.role, s.helmet, y => carrierFront(s.vest, y));
  }
  let after = 0; m.full.traverse(q => { if ((q as THREE.Mesh).isMesh) after++; });
  return after - before;
}
